import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Music2, Clock, Mail, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { submitContact } from '../lib/api';
import { ALP_SOCIAL_LOGO } from '../lib/assets';

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please complete all fields', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      toast({ title: 'Message sent', description: 'Thank you \u2014 we will respond within 24\u201348 hours.' });
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast({ title: 'Could not send message', description: 'Please try again later.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-alp-black min-h-screen">
      <div className="border-b border-alp-line">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 text-center">
          <p className="text-alp-gold text-xs tracking-[0.3em] uppercase mb-3">Get In Touch</p>
          <h1 className="font-serif text-5xl md:text-6xl text-alp-cream heading-glow">Contact</h1>
          <p className="text-alp-gray mt-4 max-w-xl mx-auto">
            For product availability or order support, please contact us directly.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">Name</Label>
              <Input value={form.name} onChange={set('name')} className="bg-alp-dark border-alp-line text-alp-cream" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">Email</Label>
              <Input type="email" value={form.email} onChange={set('email')} className="bg-alp-dark border-alp-line text-alp-cream" />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-alp-gray mb-2 block">Message</Label>
              <Textarea value={form.message} onChange={set('message')} rows={5} className="bg-alp-dark border-alp-line text-alp-cream" />
            </div>
            <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-alp-gold text-alp-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-60">
              {sending ? 'Sending\u2026' : <>Send Message <ArrowRight size={15} /></>}
            </button>
          </form>
        </div>

        <div className="space-y-6">
          <a href="https://wa.me/61435806235" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 border border-alp-line rounded p-5 bg-alp-card/40 hover:border-alp-gold/50 transition-colors">
            <MessageCircle size={24} className="text-alp-gold" />
            <div>
              <div className="text-alp-cream">WhatsApp</div>
              <div className="text-alp-gray text-sm">@australianluxe &middot; +61 435 806 235</div>
            </div>
          </a>
          <a href="mailto:support@australianluxepeptides.com" className="flex items-center gap-4 border border-alp-line rounded p-5 bg-alp-card/40 hover:border-alp-gold/50 transition-colors">
            <Mail size={24} className="text-alp-gold" />
            <div>
              <div className="text-alp-cream">Email</div>
              <div className="text-alp-gray text-sm">support@australianluxepeptides.com</div>
            </div>
          </a>
          <div className="flex items-center gap-4 border border-alp-line rounded p-5 bg-alp-card/40">
            <Clock size={24} className="text-alp-gold" />
            <div>
              <div className="text-alp-cream">Response Time</div>
              <div className="text-alp-gray text-sm">We typically respond within 24&ndash;48 hours.</div>
            </div>
          </div>
          <div className="flex items-center gap-5 px-5">
            <a href="https://www.tiktok.com/@australianluxe" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-alp-gray hover:text-alp-gold transition-colors"><Music2 size={20} /> <span className="text-sm">@australianluxe</span></a>
            <a href="https://wa.me/61435806235" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-alp-gray hover:text-alp-gold transition-colors"><img src={ALP_SOCIAL_LOGO} alt="Australian Luxe Peptides" className="w-6 h-6 rounded-full object-cover" /> <span className="text-sm">australianluxe</span></a>
          </div>
          <div className="flex gap-4 px-5 text-sm">
            <Link to="/faq" className="text-alp-gold hover:text-alp-goldlight transition-colors">View FAQ &rarr;</Link>
            <Link to="/shipping" className="text-alp-gold hover:text-alp-goldlight transition-colors">Shipping Info &rarr;</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
